import React, { useCallback, useState } from 'react';
import { useLocation, useHistory, matchPath } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Types
import { Feature } from 'Shared/types/Organization.type';

// Hooks
import { useFeature, useToggle } from 'Shared/utils/hooks';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';

// Components
import { Portal } from 'Wpds/components/portal/Portal';
import WpIcon from 'Wpds/components/icon/WpIcon';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import CreateAiOrDtModal, { ItemType } from 'Shared/components/CreateDtOrAiModal/CreateDtOrAiModal';
import GiveFeedbackOrKudosModal, {
  FlowType,
} from 'Shared/components/GiveFeedbackOrKudosModal/GiveFeedbackOrKudosModal';
import CreateWorkspaceModal from 'Shared/components/CreateWorkspaceModal/CreateWorkspaceModal';

// Styles
import './MobileAddButton.scss';

type Props = {
  children?: React.ReactNode;
  inPortal?: boolean;
};

const MobileAddButton = ({ children, inPortal = true }: Props) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { featureFlagMobileAddBtn: showButton } = useFlags();
  const { isXtraSmall } = useResponsiveScreenWidth({
    isAtMost: true,
  });

  const [recognitionFlowType, setRecognitionFlowType] = useState<FlowType>(null);
  const [createDtOrAiType, setCreateDtOrAiType] = useState<ItemType>(null);
  const [isCreateWorkspaceOpen, toggleIsCreateWorkspaceOpen] = useToggle(false);
  const [isGiveFeedbackOrKudosModalOpen, toggleIsGiveFeedbackOrKudosModalOpen] = useToggle(false);
  const { isActive: isKudosActive } = useFeature(Feature.kudos);

  const isCreateDtOrAiModalOpen = createDtOrAiType !== null;

  const isADoNotShowOnPath = matchPath(pathname, {
    path: [PageUrls.SETTINGS_PAGE, PageUrls.ADMIN, PageUrls.ANALYTICS_MANAGER_DASHBOARD, PageUrls.USER_PROFILE_PAGE],
  });

  const handleDiscussionTopicClick = useCallback(() => {
    setCreateDtOrAiType(ItemType.discussionTopic);
  }, []);

  const handleActionItemClick = useCallback(() => {
    setCreateDtOrAiType(ItemType.actionItem);
  }, []);

  const handleCreateNewGoalClick = useCallback(() => {
    history.push(PageUrls.GOALS_NEW_PAGE);
  }, []);

  const handleKudosClick = () => {
    setRecognitionFlowType('kudos');
    toggleIsGiveFeedbackOrKudosModalOpen();
  };

  const handleGiveFeedbackFlow = () => {
    setRecognitionFlowType('feedback');
    toggleIsGiveFeedbackOrKudosModalOpen();
  };

  if (!showButton) return null;
  if (!isXtraSmall) return null;
  if (isADoNotShowOnPath) return null;

  const baseClassName = 'MobileAddButton';

  const button = children ? (
    <>{children}</>
  ) : (
    <div className={baseClassName}>
      <button className={`${baseClassName}__button`}>
        <WpIcon name="plus" size="lg" />
      </button>
    </div>
  );

  const dropdown = (
    <WpDropdown>
      <WpDropdownToggle>{button}</WpDropdownToggle>
      <WpDropdownMenu>
        <WpDropdownMenuItem onClick={handleDiscussionTopicClick}>
          <WpIcon name="comment-alt-check" />
          Create Discussion Topic
        </WpDropdownMenuItem>
        <WpDropdownMenuItem onClick={handleActionItemClick}>
          <WpIcon name="check-square" />
          Create Action Item
        </WpDropdownMenuItem>
        <WpDropdownMenuItem onClick={handleCreateNewGoalClick}>
          <WpIcon name="bullseye-arrow" />
          Create Goal
        </WpDropdownMenuItem>
        <WpDropdownMenuItem onClick={toggleIsCreateWorkspaceOpen}>
          <WpIcon name="users" />
          Create Workspace
        </WpDropdownMenuItem>
        <WpDropdownMenuDivider />
        {isKudosActive && (
          <WpDropdownMenuItem onClick={handleKudosClick}>
            <WpIcon name="gem" />
            Give Kudos
          </WpDropdownMenuItem>
        )}
        <WpDropdownMenuItem onClick={handleGiveFeedbackFlow}>
          <WpIcon name="hand-holding-heart" />
          Give Feedback
        </WpDropdownMenuItem>
      </WpDropdownMenu>
    </WpDropdown>
  );

  return (
    <>
      {inPortal ? (
        <Portal>
          <>{dropdown}</>
        </Portal>
      ) : (
        <>{dropdown}</>
      )}
      {isCreateDtOrAiModalOpen && (
        <CreateAiOrDtModal
          isOpen={createDtOrAiType !== null}
          toggleIsOpen={() => setCreateDtOrAiType(null)}
          itemType={createDtOrAiType}
        />
      )}
      {isGiveFeedbackOrKudosModalOpen && (
        <GiveFeedbackOrKudosModal
          flow={recognitionFlowType}
          isOpen={isGiveFeedbackOrKudosModalOpen}
          toggleIsOpen={toggleIsGiveFeedbackOrKudosModalOpen}
        />
      )}
      {isCreateWorkspaceOpen && (
        <CreateWorkspaceModal isOpen={isCreateWorkspaceOpen} toggleModal={toggleIsCreateWorkspaceOpen} />
      )}
    </>
  );
};

export default MobileAddButton;
